import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Layout from './components/Layout';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmModal';
import api from './api';

// Code-split all pages — they load on demand instead of all upfront
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Payments = React.lazy(() => import('./pages/Payments'));
const Users = React.lazy(() => import('./pages/Users'));
const Transactions = React.lazy(() => import('./pages/Transactions'));
const ActivityLog = React.lazy(() => import('./pages/ActivityLog'));
const Announcements = React.lazy(() => import('./pages/Announcements'));
const Servers = React.lazy(() => import('./pages/Servers'));
const Plans = React.lazy(() => import('./pages/Plans'));

// Lightweight loading spinner for lazy-loaded pages
const PageLoader = () => (
    <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
    </div>
);

// Protected Route Wrapper — validates auth via HttpOnly cookie
const ProtectedRoute = ({ children }) => {
    const [authState, setAuthState] = useState('checking'); // 'checking' | 'ok' | 'unauthorized'

    useEffect(() => {
        let cancelled = false;
        api.get('/users/me')
            .then(() => { if (!cancelled) setAuthState('ok'); })
            .catch(() => { if (!cancelled) setAuthState('unauthorized'); });
        return () => { cancelled = true; };
    }, []);

    if (authState === 'checking') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
        );
    }
    if (authState === 'unauthorized') {
        return <Navigate to="/login" replace />;
    }
    return children;
};

function App() {
    return (
        <BrowserRouter>
            <ToastProvider>
                <ConfirmProvider>
                    <Routes>
                        <Route path="/login" element={<Login />} />

                        <Route path="/" element={
                            <ProtectedRoute>
                                <Layout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
                            <Route path="payments" element={<Suspense fallback={<PageLoader />}><Payments /></Suspense>} />
                            <Route path="plans" element={<Suspense fallback={<PageLoader />}><Plans /></Suspense>} />
                            <Route path="users" element={<Suspense fallback={<PageLoader />}><Users /></Suspense>} />
                            <Route path="servers" element={<Suspense fallback={<PageLoader />}><Servers /></Suspense>} />
                            <Route path="transactions" element={<Suspense fallback={<PageLoader />}><Transactions /></Suspense>} />
                            <Route path="logs" element={<Suspense fallback={<PageLoader />}><ActivityLog /></Suspense>} />
                            <Route path="announcements" element={<Suspense fallback={<PageLoader />}><Announcements /></Suspense>} />
                        </Route>
                    </Routes>
                </ConfirmProvider>
            </ToastProvider>
        </BrowserRouter>
    );
}

export default App;
